import { useState, useEffect } from "react";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { Expense } from "@/types/expense";
import { kvService } from "@/lib/kv-service";
import { realtimeService, RealtimeEvent } from "@/lib/realtime-service";
import { cacheService } from "@/lib/cache-service";
import { DEFAULT_JOBS } from "@/data/test-data";
import { generateUUID } from "@/lib/uuid";

// Always use Cloudflare KV for data storage
const dataService = kvService;

export function useDataService() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [jobs, setJobs] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const [isRealtimeEnabled, setIsRealtimeEnabled] = useState(false);

  useEffect(() => {
    loadData();
    
    // Subscribe to real-time events
    const unsubscribe = realtimeService.subscribe(handleRealtimeEvent);
    
    // Check real-time connection status
    const checkConnection = () => {
      setIsRealtimeConnected(realtimeService.isConnectedToRealtime());
      setIsRealtimeEnabled(realtimeService.isRealtimeEnabled());
    };
    
    checkConnection();
    const connectionInterval = setInterval(checkConnection, 5000);
    
    return () => {
      unsubscribe();
      clearInterval(connectionInterval);
    };
  }, []);

  const handleRealtimeEvent = (event: RealtimeEvent) => {
    switch (event.type) {
      case 'customer_added':
        setCustomers(prev => {
          // Avoid duplicates by ID or customer code
          if (prev.some(c => c.id === event.data.id || c.customerCode === event.data.customerCode)) {
            return prev;
          }
          cacheService.putItem('customers', event.data);
          return [event.data, ...prev];
        });
        break;
        
      case 'customer_updated':
        setCustomers(prev => 
          prev.map(c => c.id === event.data.id ? event.data : c)
        );
        cacheService.putItem('customers', event.data);
        break;
        
      case 'customer_deleted':
        setCustomers(prev => prev.filter(c => c.id !== event.data.id));
        cacheService.deleteItem('customers', event.data.id);
        break;
        
      case 'product_added':
        setProducts(prev => {
          // Avoid duplicates
          if (prev.some(p => p.id === event.data.id)) return prev;
          cacheService.putItem('products', event.data);
          return [event.data, ...prev];
        });
        break;
        
      case 'product_updated':
        setProducts(prev => 
          prev.map(p => p.id === event.data.id ? event.data : p)
        );
        cacheService.putItem('products', event.data);
        break;
        
      case 'product_deleted':
        setProducts(prev => prev.filter(p => p.id !== event.data.id));
        cacheService.deleteItem('products', event.data.id);
        break;
        
      case 'jobs_updated':
        if (Array.isArray(event.data)) {
          // Only update if the data is different from current state
          // This prevents unnecessary re-renders from our own updates
          setJobs(prev => {
            const prevStr = JSON.stringify([...prev].sort());
            const newStr = JSON.stringify([...event.data].sort());
            if (prevStr !== newStr) {
              cacheService.saveJobs(event.data);
              return event.data;
            }
            return prev;
          });
        }
        break;
        
      case 'payment_made':
        // Payment events are handled by product_updated events
        break;
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [customersData, productsData, expensesData, jobsData] = await Promise.all([
        dataService.getCustomers(),
        dataService.getProducts(),
        dataService.getExpenses(),
        dataService.getJobs(),
      ]);

      setCustomers(customersData);
      setProducts(productsData);
      setExpenses(expensesData);
      
      // Jobs are managed manually by user - no default initialization
      setJobs(jobsData);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Customer methods
  const addCustomer = async (customerData: Omit<Customer, "id" | "createdAt">) => {
    const newCustomer: Customer = {
      ...customerData,
      id: generateUUID(),
      createdAt: new Date(),
    };

    try {
      // Optimistic update - add immediately for UI feedback
      setCustomers((prev) => {
        // Check if customer with same code already exists to prevent duplicates
        if (prev.some(c => c.customerCode === newCustomer.customerCode)) {
          console.warn("Customer with this code already exists, skipping optimistic update");
          return prev;
        }
        return [newCustomer, ...prev];
      });
      
      await dataService.createCustomer(newCustomer);
      return newCustomer;
    } catch (error) {
      console.error("Failed to add customer:", error);
      // Revert optimistic update on error
      setCustomers((prev) => prev.filter(c => c.id !== newCustomer.id));
      throw error;
    }
  };

  const updateCustomer = async (updatedCustomer: Customer) => {
    // Store previous state for rollback
    const previousCustomers = customers;
    
    try {
      // Optimistic update
      setCustomers((prev) =>
        prev.map((c) => (c.id === updatedCustomer.id ? updatedCustomer : c))
      );
      
      await dataService.updateCustomer(updatedCustomer);
      // Real-time update will be handled by the event listener
    } catch (error) {
      console.error("Failed to update customer:", error);
      // Rollback on error
      setCustomers(previousCustomers);
      throw error;
    }
  };

  const deleteCustomer = async (id: string) => {
    // Store previous state for rollback
    const previousCustomers = customers;
    
    try {
      // Optimistic update
      setCustomers((prev) => prev.filter((c) => c.id !== id));
      
      await dataService.deleteCustomer(id);
      // Real-time update will be handled by the event listener
    } catch (error) {
      console.error("Failed to delete customer:", error);
      // Rollback on error
      setCustomers(previousCustomers);
      throw error;
    }
  };

  // Product methods
  const addProduct = async (productData: Omit<Product, "id" | "createdAt" | "remainingAmount">) => {
    const newProduct: Product = {
      ...productData,
      id: generateUUID(),
      deviceDescription: productData.deviceDescription || "",
      installments: productData.installments || 0,
      remainingAmount: productData.salePrice - productData.paidAmount,
      createdAt: new Date(),
    };

    try {
      // Optimistic update FIRST - add immediately for UI feedback
      setProducts((prev) => {
        // Check if product with same ID already exists to prevent duplicates
        if (prev.some(p => p.id === newProduct.id)) {
          console.warn("Product with this ID already exists, skipping optimistic update");
          return prev;
        }
        return [newProduct, ...prev];
      });
      
      await dataService.createProduct(newProduct);
      // Real-time event will be ignored because we already have this product (duplicate check in handleRealtimeEvent)
      return newProduct;
    } catch (error) {
      console.error("Failed to add product:", error);
      // Revert optimistic update on error
      setProducts((prev) => prev.filter(p => p.id !== newProduct.id));
      throw error;
    }
  };

  const updateProduct = async (updatedProduct: Product) => {
    // Store previous state for rollback
    const previousProducts = products;
    
    try {
      // Optimistic update
      setProducts((prev) =>
        prev.map((p) => (p.id === updatedProduct.id ? updatedProduct : p))
      );
      
      await dataService.updateProduct(updatedProduct);
      // Real-time update will be handled by the event listener
    } catch (error) {
      console.error("Failed to update product:", error);
      // Rollback on error
      setProducts(previousProducts);
      throw error;
    }
  };

  const deleteProduct = async (id: string) => {
    // Store previous state for rollback
    const previousProducts = products;
    
    try {
      // Optimistic update
      setProducts((prev) => prev.filter((p) => p.id !== id));
      
      await dataService.deleteProduct(id);
      // Real-time update will be handled by the event listener
    } catch (error) {
      console.error("Failed to delete product:", error);
      // Rollback on error
      setProducts(previousProducts);
      throw error;
    }
  };

  const updatePayment = async (id: string, paidAmount: number) => {
    const product = products.find((p) => p.id === id);
    if (!product) return;

    const updatedProduct = {
      ...product,
      paidAmount,
      remainingAmount: product.salePrice - paidAmount,
    };

    await updateProduct(updatedProduct);
  };

  const handlePayment = async (productId: string, amount: number) => {
    const product = products.find((p) => p.id === productId);
    if (!product || amount <= 0) return;

    const newPaidAmount = product.paidAmount + amount;
    const updatedProduct = {
      ...product,
      paidAmount: Math.min(product.salePrice, newPaidAmount),
      remainingAmount: Math.max(0, product.salePrice - newPaidAmount),
      installments: (product.installments || 0) + 1,
    };

    await updateProduct(updatedProduct);
  };

  // Jobs methods
  const addJob = async (job: string) => {
    const updatedJobs = [...jobs, job];
    
    try {
      // Update locally and in database
      setJobs(updatedJobs);
      await dataService.updateJobs(updatedJobs);
      // Note: Real-time event will be received but ignored since we already updated
    } catch (error) {
      console.error("Failed to add job:", error);
      // Rollback on error
      setJobs(jobs);
      throw error;
    }
  };

  const deleteJob = async (job: string) => {
    const updatedJobs = jobs.filter((j) => j !== job);
    const previousJobs = jobs;
    
    try {
      // Update locally and in database
      setJobs(updatedJobs);
      await dataService.updateJobs(updatedJobs);
      // Note: Real-time event will be received but ignored since we already updated
    } catch (error) {
      console.error("Failed to delete job:", error);
      // Rollback on error
      setJobs(previousJobs);
      throw error;
    }
  };

  // Expense methods
  const addExpense = async (expenseData: Omit<Expense, "id" | "createdAt">) => {
    const newExpense: Expense = {
      ...expenseData,
      id: generateUUID(),
      createdAt: new Date(),
    };

    try {
      setExpenses((prev) => [newExpense, ...prev]);
      await dataService.createExpense(newExpense);
      return newExpense;
    } catch (error) {
      console.error("Failed to add expense:", error);
      setExpenses((prev) => prev.filter(e => e.id !== newExpense.id));
      throw error;
    }
  };

  const updateExpense = async (updatedExpense: Expense) => {
    const previousExpenses = expenses;
    
    try {
      setExpenses((prev) =>
        prev.map((e) => (e.id === updatedExpense.id ? updatedExpense : e))
      );
      await dataService.updateExpense(updatedExpense);
    } catch (error) {
      console.error("Failed to update expense:", error);
      setExpenses(previousExpenses);
      throw error;
    }
  };

  const deleteExpense = async (id: string) => {
    const previousExpenses = expenses;
    
    try {
      setExpenses((prev) => prev.filter((e) => e.id !== id));
      await dataService.deleteExpense(id);
    } catch (error) {
      console.error("Failed to delete expense:", error);
      setExpenses(previousExpenses);
      throw error;
    }
  };

  return {
    // Data
    customers,
    products,
    expenses,
    jobs,
    isLoading,
    isRealtimeConnected,
    isRealtimeEnabled,
    
    // Methods
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addProduct,
    updateProduct,
    deleteProduct,
    updatePayment,
    handlePayment,
    addExpense,
    updateExpense,
    deleteExpense,
    addJob,
    deleteJob,
    
    // Utility
    refreshData: loadData,
  };
}